'use client';

import { forwardRef, useImperativeHandle, useState } from 'react';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

const MatrizQuestion = forwardRef(
  (
    {
      matrixOption,
      formValues,
      index,
      setFormValues,
      questionName = 'Pregunta de Matriz',
      description = 'Selecciona las opciones correspondientes',
      primaryColor = '#1976d2',
      fontFamily,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const isFixed = matrixOption.matrixType === 'fixed';
    const isDynamic = matrixOption.matrixType === 'dynamic';
    const isRadio = matrixOption.inputType === 'single';
    const isCheckbox = matrixOption.inputType === 'multiple';
    const hasOtherOption = matrixOption.hasOtherOption;
    const otherOptionDescription =
      matrixOption.otherOptionDescription || 'Otra opción';

    const [showOtherOption, setShowOtherOption] = useState(false);
    const [otherOptionText, setOtherOptionText] = useState('');
    const MAX_OTHER_OPTIONS = 1;

    const [dynamicRows, setDynamicRows] = useState([
      { id: 0, selectedOption: '' },
    ]);
    const [invalidRows, setInvalidRows] = useState(new Set());
    const [otherOptions, setOtherOptions] = useState([]);

    useImperativeHandle(ref, () => ({
      validate: () => {
        const newInvalidRows = new Set();
        let isValid = true;

        if (isFixed) {
          matrixOption.options.forEach((option) => {
            const hasSelection = formValues.selectionValuesMatriz?.[option];
            const isValidSelection = isRadio
              ? hasSelection && hasSelection !== ''
              : hasSelection &&
                Array.isArray(hasSelection) &&
                hasSelection.length > 0;

            if (!isValidSelection) {
              newInvalidRows.add(option);
              isValid = false;
            }
          });

          if (hasOtherOption) {
            otherOptions.forEach((otherOpt) => {
              if (otherOpt.show) {
                if (!otherOpt.text.trim()) {
                  newInvalidRows.add(`other-option-text-${otherOpt.id}`);
                  isValid = false;
                }

                const key =
                  otherOpt.text.trim() || `temp-other-option-${otherOpt.id}`;
                const hasOtherSelection =
                  formValues.selectionValuesMatriz?.[key];
                const isValidOtherSelection = isRadio
                  ? hasOtherSelection &&
                    Array.isArray(hasOtherSelection) &&
                    hasOtherSelection.length > 0
                  : hasOtherSelection &&
                    Array.isArray(hasOtherSelection) &&
                    hasOtherSelection.length > 0;

                if (!isValidOtherSelection) {
                  newInvalidRows.add(`other-option-${otherOpt.id}`);
                  isValid = false;
                }
              }
            });
          }
        } else if (isDynamic) {
          dynamicRows.forEach((row) => {
            if (row.selectedOption) {
              const hasSelection =
                formValues.selectionValuesMatriz?.[row.selectedOption];
              const isValidSelection = isRadio
                ? hasSelection && hasSelection !== ''
                : hasSelection &&
                  Array.isArray(hasSelection) &&
                  hasSelection.length > 0;

              if (!isValidSelection) {
                newInvalidRows.add(row.selectedOption);
                isValid = false;
              }
            } else {
              newInvalidRows.add(`row-${row.id}`);
              isValid = false;
            }
          });

          if (showOtherOption && hasOtherOption) {
            if (!otherOptionText.trim()) {
              newInvalidRows.add('other-option-text');
              isValid = false;
            }

            const key = otherOptionText.trim() || 'temp-other-option';
            const hasOtherSelection = formValues.selectionValuesMatriz?.[key];
            const isValidOtherSelection = isRadio
              ? hasOtherSelection &&
                Array.isArray(hasOtherSelection) &&
                hasOtherSelection.length > 0
              : hasOtherSelection &&
                Array.isArray(hasOtherSelection) &&
                hasOtherSelection.length > 0;

            if (!isValidOtherSelection) {
              newInvalidRows.add('other-option');
              isValid = false;
            }
          }
        }

        setInvalidRows(newInvalidRows);
        return isValid;
      },
    }));

    const handleFixedRadioChange = (rowValue, columnValue) => {
      setFormValues((prev) => {
        const updated = [...prev];
        updated[index].selectionValuesMatriz = {
          ...updated[index].selectionValuesMatriz,
          [rowValue]: [columnValue],
        };
        return updated;
      });

      setInvalidRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowValue);
        return newSet;
      });
    };

    const handleFixedCheckboxChange = (rowValue, columnValue, checked) => {
      setFormValues((prev) => {
        const updated = [...prev];
        const prevVals = updated[index].selectionValuesMatriz?.[rowValue] || [];
        updated[index].selectionValuesMatriz = {
          ...updated[index].selectionValuesMatriz,
          [rowValue]: checked
            ? [...prevVals, columnValue]
            : prevVals.filter((v) => v !== columnValue),
        };
        return updated;
      });

      setTimeout(() => {
        const currentVals = formValues.selectionValuesMatriz?.[rowValue] || [];
        if (currentVals.length > 0) {
          setInvalidRows((prev) => {
            const newSet = new Set(prev);
            newSet.delete(rowValue);
            return newSet;
          });
        }
      }, 0);
    };

    const handleDynamicSelectChange = (rowId, selectedOption) => {
      setDynamicRows((prev) =>
        prev.map((row) =>
          row.id === rowId ? { ...row, selectedOption } : row,
        ),
      );

      setInvalidRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`row-${rowId}`);
        return newSet;
      });
    };

    const handleDynamicRadioChange = (rowId, value) => {
      const row = dynamicRows.find((r) => r.id === rowId);
      if (row && row.selectedOption) {
        setFormValues((prev) => {
          const updated = [...prev];
          updated[index].selectionValuesMatriz = {
            ...updated[index].selectionValuesMatriz,
            [row.selectedOption]: [value],
          };
          return updated;
        });

        setInvalidRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(row.selectedOption);
          return newSet;
        });
      }
    };

    const handleDynamicCheckboxChange = (rowId, value, checked) => {
      const row = dynamicRows.find((r) => r.id === rowId);
      if (row && row.selectedOption) {
        setFormValues((prev) => {
          const updated = [...prev];
          const prevVals =
            updated[index].selectionValuesMatriz?.[row.selectedOption] || [];
          updated[index].selectionValuesMatriz = {
            ...updated[index].selectionValuesMatriz,
            [row.selectedOption]: checked
              ? [...prevVals, value]
              : prevVals.filter((v) => v !== value),
          };
          return updated;
        });

        setTimeout(() => {
          const currentVals =
            formValues.selectionValuesMatriz?.[row.selectedOption] || [];
          if (currentVals.length > 0) {
            setInvalidRows((prev) => {
              const newSet = new Set(prev);
              newSet.delete(row.selectedOption);
              return newSet;
            });
          }
        }, 0);
      }
    };

    const handleOtherOptionTextChangeDynamic = (value) => {
      const oldKey = otherOptionText.trim() || 'temp-other-option';
      const newKey = value.trim() || 'temp-other-option';

      setOtherOptionText(value);

      if (oldKey !== newKey) {
        setFormValues((prev) => {
          const updated = [...prev];
          const selections = updated[index].selectionValuesMatriz;

          if (selections && selections[oldKey]) {
            const oldSelections = selections[oldKey];
            const newSelections = { ...selections };
            delete newSelections[oldKey];
            newSelections[newKey] = oldSelections;
            updated[index].selectionValuesMatriz = newSelections;
          }

          return updated;
        });
      }

      if (value.trim()) {
        setInvalidRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete('other-option-text');
          return newSet;
        });
      }
    };

    const handleOtherOptionRadioChangeDynamic = (value) => {
      const key = otherOptionText.trim() || 'temp-other-option';
      setFormValues((prev) => {
        const updated = [...prev];
        updated[index].selectionValuesMatriz = {
          ...updated[index].selectionValuesMatriz,
          [key]: [value],
        };
        return updated;
      });

      setInvalidRows((prev) => {
        const s = new Set(prev);
        s.delete('other-option');
        return s;
      });
    };

    const handleOtherOptionCheckboxChangeDynamic = (value, checked) => {
      const key = otherOptionText.trim() || 'temp-other-option';
      setFormValues((prev) => {
        const updated = [...prev];
        const prevVals = updated[index].selectionValuesMatriz?.[key] || [];
        updated[index].selectionValuesMatriz = {
          ...updated[index].selectionValuesMatriz,
          [key]: checked
            ? [...prevVals, value]
            : prevVals.filter((v) => v !== value),
        };
        return updated;
      });

      if (checked) {
        setInvalidRows((prev) => {
          const s = new Set(prev);
          s.delete('other-option');
          return s;
        });
      }
    };

    const toggleOtherOptionDynamic = () => {
      if (showOtherOption) {
        const currentKey = otherOptionText.trim() || 'temp-other-option';
        setOtherOptionText('');
        setFormValues((prev) => {
          const updated = [...prev];
          const sv = { ...updated[index].selectionValuesMatriz };
          delete sv[currentKey];
          delete sv['temp-other-option'];
          updated[index].selectionValuesMatriz = sv;
          return updated;
        });
        setInvalidRows((prev) => {
          const s = new Set(prev);
          s.delete('other-option');
          s.delete('other-option-text');
          return s;
        });
      }
      setShowOtherOption(!showOtherOption);
    };

    const handleOtherOptionTextChangeFixed = (otherOptId, value) => {
      setOtherOptions((prev) =>
        prev.map((opt) =>
          opt.id === otherOptId ? { ...opt, text: value } : opt,
        ),
      );

      const otherOpt = otherOptions.find((o) => o.id === otherOptId);
      const oldKey = otherOpt?.text.trim() || `temp-other-option-${otherOptId}`;
      const newKey = value.trim() || `temp-other-option-${otherOptId}`;

      if (oldKey !== newKey) {
        setFormValues((prev) => {
          const updated = [...prev];
          const selections = updated[index].selectionValuesMatriz;

          if (selections && selections[oldKey]) {
            const oldSelections = selections[oldKey];
            const newSelections = { ...selections };
            delete newSelections[oldKey];
            newSelections[newKey] = oldSelections;
            updated[index].selectionValuesMatriz = newSelections;
          }

          return updated;
        });
      }

      if (value.trim()) {
        setInvalidRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(`other-option-text-${otherOptId}`);
          return newSet;
        });
      }
    };

    const handleOtherOptionRadioChangeFixed = (otherOptId, value) => {
      const otherOpt = otherOptions.find((o) => o.id === otherOptId);
      const key = otherOpt?.text.trim() || `temp-other-option-${otherOptId}`;

      setFormValues((prev) => {
        const updated = [...prev];
        updated[index].selectionValuesMatriz = {
          ...updated[index].selectionValuesMatriz,
          [key]: [value],
        };
        return updated;
      });

      setInvalidRows((prev) => {
        const s = new Set(prev);
        s.delete(`other-option-${otherOptId}`);
        return s;
      });
    };

    const handleOtherOptionCheckboxChangeFixed = (
      otherOptId,
      value,
      checked,
    ) => {
      const otherOpt = otherOptions.find((o) => o.id === otherOptId);
      const key = otherOpt?.text.trim() || `temp-other-option-${otherOptId}`;

      setFormValues((prev) => {
        const updated = [...prev];
        const prevVals = updated[index].selectionValuesMatriz?.[key] || [];
        updated[index].selectionValuesMatriz = {
          ...updated[index].selectionValuesMatriz,
          [key]: checked
            ? [...prevVals, value]
            : prevVals.filter((v) => v !== value),
        };
        return updated;
      });

      if (checked) {
        setInvalidRows((prev) => {
          const s = new Set(prev);
          s.delete(`other-option-${otherOptId}`);
          return s;
        });
      }
    };

    const addOtherOptionFixed = () => {
      if (otherOptions.length < MAX_OTHER_OPTIONS) {
        const newId =
          otherOptions.length > 0
            ? Math.max(...otherOptions.map((o) => o.id)) + 1
            : 0;
        setOtherOptions((prev) => [
          ...prev,
          { id: newId, text: '', show: true },
        ]);
      }
    };

    const removeOtherOptionFixed = (otherOptId) => {
      const otherOpt = otherOptions.find((o) => o.id === otherOptId);
      const key = otherOpt?.text.trim() || `temp-other-option-${otherOptId}`;

      setFormValues((prev) => {
        const updated = [...prev];
        const sv = { ...updated[index].selectionValuesMatriz };
        delete sv[key];
        delete sv[`temp-other-option-${otherOptId}`];
        updated[index].selectionValuesMatriz = sv;
        return updated;
      });

      setInvalidRows((prev) => {
        const s = new Set(prev);
        s.delete(`other-option-${otherOptId}`);
        s.delete(`other-option-text-${otherOptId}`);
        return s;
      });

      setOtherOptions((prev) => prev.filter((opt) => opt.id !== otherOptId));
    };

    const addDynamicRow = () => {
      const newId = Math.max(...dynamicRows.map((r) => r.id)) + 1;
      setDynamicRows((prev) => [...prev, { id: newId, selectedOption: '' }]);
    };

    const removeDynamicRow = (rowId) => {
      if (dynamicRows.length > 1) {
        setDynamicRows((prev) => prev.filter((row) => row.id !== rowId));

        const row = dynamicRows.find((r) => r.id === rowId);
        if (row && row.selectedOption) {
          setFormValues((prev) => {
            const updated = [...prev];
            const newselectionValuesMatriz = {
              ...updated[index].selectionValuesMatriz,
            };
            delete newselectionValuesMatriz[row.selectedOption];
            updated[index].selectionValuesMatriz = newselectionValuesMatriz;
            return updated;
          });

          setInvalidRows((prev) => {
            const newSet = new Set(prev);
            newSet.delete(row.selectedOption);
            newSet.delete(`row-${row.id}`);
            return newSet;
          });
        }
      }
    };

    const getAvailableOptions = (currentRowId, options) => {
      const selectedOptions = dynamicRows
        .filter((row) => row.id !== currentRowId && row.selectedOption)
        .map((row) => row.selectedOption);
      return options.filter((option) => !selectedOptions.includes(option));
    };

    // ============================================
    // RENDER MÓVIL - MATRIZ FIJA
    // ============================================

    const renderFixedMatrixMobile = () => {
      return (
        <Box sx={{ mt: 2 }}>
          {matrixOption.options.map((option) => {
            const isRowInvalid = invalidRows.has(option);

            return (
              <Card
                key={option}
                elevation={isRowInvalid ? 0 : 1}
                sx={{
                  mb: 2.5,
                  border: isRowInvalid
                    ? '2px solid #d32f2f'
                    : '1px solid #e0e0e0',
                  backgroundColor: isRowInvalid ? '#ffebee' : '#fff',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: isRowInvalid
                      ? 'none'
                      : '0 4px 12px rgba(0,0,0,0.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: isRowInvalid
                      ? 'rgba(211, 47, 47, 0.08)'
                      : 'rgba(25, 118, 210, 0.04)',
                    borderBottom: '1px solid',
                    borderColor: isRowInvalid ? '#ffcdd2' : '#e3f2fd',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: isRowInvalid ? '#d32f2f' : '#1976d2',
                      fontSize: '15px',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {option}
                  </Typography>
                </Box>

                <CardContent sx={{ px: 2, py: 2, '&:last-child': { pb: 2 } }}>
                  {isRadio ? (
                    <RadioGroup
                      value={
                        formValues.selectionValuesMatriz?.[option]?.[0] || ''
                      }
                      onChange={(e) =>
                        handleFixedRadioChange(option, e.target.value)
                      }
                    >
                      {matrixOption.values.map((value, idx) => (
                        <Box
                          key={value}
                          sx={{
                            borderBottom:
                              idx < matrixOption.values.length - 1
                                ? '1px solid #f5f5f5'
                                : 'none',
                            py: 0.5,
                          }}
                        >
                          <FormControlLabel
                            value={value}
                            control={
                              <Radio
                                sx={{
                                  color: isRowInvalid
                                    ? '#d32f2f'
                                    : primaryColor,
                                  '&.Mui-checked': {
                                    color: primaryColor,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  color: '#424242',
                                  fontWeight: 400,
                                }}
                              >
                                {value}
                              </Typography>
                            }
                            sx={{
                              width: '100%',
                              mx: 0,
                              my: 0,
                              py: 0.75,
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                borderRadius: 1,
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </RadioGroup>
                  ) : (
                    <Box>
                      {matrixOption.values.map((value, idx) => (
                        <Box
                          key={value}
                          sx={{
                            borderBottom:
                              idx < matrixOption.values.length - 1
                                ? '1px solid #f5f5f5'
                                : 'none',
                            py: 0.5,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  formValues.selectionValuesMatriz?.[
                                    option
                                  ]?.includes(value) || false
                                }
                                onChange={(e) =>
                                  handleFixedCheckboxChange(
                                    option,
                                    value,
                                    e.target.checked,
                                  )
                                }
                                sx={{
                                  color: isRowInvalid
                                    ? '#d32f2f'
                                    : primaryColor,
                                  '&.Mui-checked': {
                                    color: primaryColor,
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  color: '#424242',
                                  fontWeight: 400,
                                }}
                              >
                                {value}
                              </Typography>
                            }
                            sx={{
                              width: '100%',
                              mx: 0,
                              my: 0,
                              py: 0.75,
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                borderRadius: 1,
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Otras opciones - Fijas */}
          {otherOptions.map((otherOpt) => {
            const key =
              otherOpt.text.trim() || `temp-other-option-${otherOpt.id}`;
            const isOtherRowInvalid =
              invalidRows.has(`other-option-${otherOpt.id}`) ||
              invalidRows.has(`other-option-text-${otherOpt.id}`);

            return (
              <Card
                key={`other-option-${otherOpt.id}`}
                elevation={isOtherRowInvalid ? 0 : 2}
                sx={{
                  mb: 2.5,
                  border: isOtherRowInvalid
                    ? '2px solid #d32f2f'
                    : '2px solid #1976d2',
                  backgroundColor: isOtherRowInvalid ? '#ffebee' : '#f8fbff',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: isOtherRowInvalid
                      ? 'rgba(211, 47, 47, 0.08)'
                      : 'rgba(25, 118, 210, 0.08)',
                    borderBottom: '1px solid',
                    borderColor: isOtherRowInvalid ? '#ffcdd2' : '#bbdefb',
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: isOtherRowInvalid ? '#d32f2f' : '#1976d2',
                      fontSize: '15px',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {otherOptionDescription}
                  </Typography>
                  <IconButton
                    onClick={() => removeOtherOptionFixed(otherOpt.id)}
                    size="small"
                    sx={{
                      color: '#d32f2f',
                      '&:hover': {
                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <CardContent sx={{ px: 2, py: 2, '&:last-child': { pb: 2 } }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Especificar..."
                    value={otherOpt.text}
                    onChange={(e) =>
                      handleOtherOptionTextChangeFixed(
                        otherOpt.id,
                        e.target.value,
                      )
                    }
                    error={invalidRows.has(`other-option-text-${otherOpt.id}`)}
                    helperText={
                      invalidRows.has(`other-option-text-${otherOpt.id}`)
                        ? 'Este campo es obligatorio'
                        : ''
                    }
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                      },
                    }}
                  />

                  {isRadio ? (
                    <RadioGroup
                      value={formValues.selectionValuesMatriz?.[key]?.[0] || ''}
                      onChange={(e) =>
                        handleOtherOptionRadioChangeFixed(
                          otherOpt.id,
                          e.target.value,
                        )
                      }
                    >
                      {matrixOption.values.map((value, idx) => (
                        <Box
                          key={value}
                          sx={{
                            borderBottom:
                              idx < matrixOption.values.length - 1
                                ? '1px solid #f5f5f5'
                                : 'none',
                            py: 0.5,
                          }}
                        >
                          <FormControlLabel
                            value={value}
                            control={
                              <Radio
                                sx={{
                                  color: isOtherRowInvalid
                                    ? '#d32f2f'
                                    : primaryColor,
                                  '&.Mui-checked': { color: primaryColor },
                                }}
                              />
                            }
                            label={
                              <Typography
                                sx={{ fontSize: '14px', color: '#424242' }}
                              >
                                {value}
                              </Typography>
                            }
                            sx={{
                              width: '100%',
                              mx: 0,
                              my: 0,
                              py: 0.75,
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                borderRadius: 1,
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </RadioGroup>
                  ) : (
                    <Box>
                      {matrixOption.values.map((value, idx) => (
                        <Box
                          key={value}
                          sx={{
                            borderBottom:
                              idx < matrixOption.values.length - 1
                                ? '1px solid #f5f5f5'
                                : 'none',
                            py: 0.5,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  formValues.selectionValuesMatriz?.[
                                    key
                                  ]?.includes(value) || false
                                }
                                onChange={(e) =>
                                  handleOtherOptionCheckboxChangeFixed(
                                    otherOpt.id,
                                    value,
                                    e.target.checked,
                                  )
                                }
                                sx={{
                                  color: isOtherRowInvalid
                                    ? '#d32f2f'
                                    : primaryColor,
                                  '&.Mui-checked': { color: primaryColor },
                                }}
                              />
                            }
                            label={
                              <Typography
                                sx={{ fontSize: '14px', color: '#424242' }}
                              >
                                {value}
                              </Typography>
                            }
                            sx={{
                              width: '100%',
                              mx: 0,
                              my: 0,
                              py: 0.75,
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                borderRadius: 1,
                              },
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      );
    };

    // ============================================
    // RENDER MÓVIL - MATRIZ DINÁMICA
    // ============================================

    const renderDynamicMatrixMobile = () => {
      return (
        <Box sx={{ mt: 2 }}>
          {dynamicRows.map((row) => {
            const availableOptions = getAvailableOptions(
              row.id,
              matrixOption.options,
            );
            const isRowInvalid =
              invalidRows.has(row.selectedOption) ||
              invalidRows.has(`row-${row.id}`);

            return (
              <Card
                key={row.id}
                elevation={isRowInvalid ? 0 : 1}
                sx={{
                  mb: 2.5,
                  border: isRowInvalid
                    ? '2px solid #d32f2f'
                    : '1px solid #e0e0e0',
                  backgroundColor: isRowInvalid ? '#ffebee' : '#fff',
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: isRowInvalid
                      ? 'none'
                      : '0 4px 12px rgba(0,0,0,0.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: isRowInvalid
                      ? 'rgba(211, 47, 47, 0.08)'
                      : 'rgba(25, 118, 210, 0.04)',
                    borderBottom: '1px solid',
                    borderColor: isRowInvalid ? '#ffcdd2' : '#e3f2fd',
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      options={availableOptions}
                      value={row.selectedOption || null}
                      onChange={(_, newValue) =>
                        handleDynamicSelectChange(row.id, newValue)
                      }
                      componentsProps={{
                        paper: {
                          sx: {
                            fontFamily,
                            '& .MuiAutocomplete-option': {
                              fontFamily,
                            },
                          },
                        },
                      }}
                      fullWidth
                      disableClearable
                      getOptionLabel={(option) => option || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Seleccionar opción..."
                          size="small"
                          error={isRowInvalid}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff',
                              fontSize: '14px',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  {dynamicRows.length > 1 && (
                    <IconButton
                      onClick={() => removeDynamicRow(row.id)}
                      size="small"
                      sx={{
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.08)',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {row.selectedOption && (
                  <CardContent sx={{ px: 2, py: 2, '&:last-child': { pb: 2 } }}>
                    {isRadio ? (
                      <RadioGroup
                        value={
                          formValues.selectionValuesMatriz?.[
                            row.selectedOption
                          ]?.[0] || ''
                        }
                        onChange={(e) =>
                          handleDynamicRadioChange(row.id, e.target.value)
                        }
                      >
                        {matrixOption.values.map((val, idx) => (
                          <Box
                            key={val}
                            sx={{
                              borderBottom:
                                idx < matrixOption.values.length - 1
                                  ? '1px solid #f5f5f5'
                                  : 'none',
                              py: 0.5,
                            }}
                          >
                            <FormControlLabel
                              value={val}
                              control={
                                <Radio
                                  sx={{
                                    color: isRowInvalid
                                      ? '#d32f2f'
                                      : primaryColor,
                                    '&.Mui-checked': { color: primaryColor },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{ fontSize: '14px', color: '#424242' }}
                                >
                                  {val}
                                </Typography>
                              }
                              sx={{
                                width: '100%',
                                mx: 0,
                                my: 0,
                                py: 0.75,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                  borderRadius: 1,
                                },
                              }}
                            />
                          </Box>
                        ))}
                      </RadioGroup>
                    ) : (
                      <Box>
                        {matrixOption.values.map((val, idx) => (
                          <Box
                            key={val}
                            sx={{
                              borderBottom:
                                idx < matrixOption.values.length - 1
                                  ? '1px solid #f5f5f5'
                                  : 'none',
                              py: 0.5,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    formValues.selectionValuesMatriz?.[
                                      row.selectedOption
                                    ]?.includes(val) || false
                                  }
                                  onChange={(e) =>
                                    handleDynamicCheckboxChange(
                                      row.id,
                                      val,
                                      e.target.checked,
                                    )
                                  }
                                  sx={{
                                    color: isRowInvalid
                                      ? '#d32f2f'
                                      : primaryColor,
                                    '&.Mui-checked': { color: primaryColor },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{ fontSize: '14px', color: '#424242' }}
                                >
                                  {val}
                                </Typography>
                              }
                              sx={{
                                width: '100%',
                                mx: 0,
                                my: 0,
                                py: 0.75,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                  borderRadius: 1,
                                },
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                )}

                {!row.selectedOption && (
                  <CardContent sx={{ px: 2, py: 2, '&:last-child': { pb: 2 } }}>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: '#999',
                        fontStyle: 'italic',
                        textAlign: 'center',
                      }}
                    >
                      Selecciona una opción para continuar
                    </Typography>
                  </CardContent>
                )}
              </Card>
            );
          })}

          {/* Otra opción - Dinámica */}
          {showOtherOption && hasOtherOption && (
            <Card
              elevation={
                invalidRows.has('other-option') ||
                invalidRows.has('other-option-text')
                  ? 0
                  : 2
              }
              sx={{
                mb: 2.5,
                border:
                  invalidRows.has('other-option') ||
                  invalidRows.has('other-option-text')
                    ? '2px solid #d32f2f'
                    : '2px solid #1976d2',
                backgroundColor:
                  invalidRows.has('other-option') ||
                  invalidRows.has('other-option-text')
                    ? '#ffebee'
                    : '#f8fbff',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  backgroundColor:
                    invalidRows.has('other-option') ||
                    invalidRows.has('other-option-text')
                      ? 'rgba(211, 47, 47, 0.08)'
                      : 'rgba(25, 118, 210, 0.08)',
                  borderBottom: '1px solid',
                  borderColor:
                    invalidRows.has('other-option') ||
                    invalidRows.has('other-option-text')
                      ? '#ffcdd2'
                      : '#bbdefb',
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color:
                      invalidRows.has('other-option') ||
                      invalidRows.has('other-option-text')
                        ? '#d32f2f'
                        : '#1976d2',
                    fontSize: '15px',
                    letterSpacing: '0.01em',
                  }}
                >
                  {otherOptionDescription}
                </Typography>
                <IconButton
                  onClick={toggleOtherOptionDynamic}
                  size="small"
                  sx={{
                    color: '#d32f2f',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.08)',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <CardContent sx={{ px: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Especificar..."
                  value={otherOptionText}
                  onChange={(e) =>
                    handleOtherOptionTextChangeDynamic(e.target.value)
                  }
                  error={invalidRows.has('other-option-text')}
                  helperText={
                    invalidRows.has('other-option-text')
                      ? 'Este campo es obligatorio'
                      : ''
                  }
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                    },
                  }}
                />

                {isRadio ? (
                  <RadioGroup
                    value={
                      formValues.selectionValuesMatriz?.[
                        otherOptionText.trim() || 'temp-other-option'
                      ]?.[0] || ''
                    }
                    onChange={(e) =>
                      handleOtherOptionRadioChangeDynamic(e.target.value)
                    }
                  >
                    {matrixOption.values.map((val, idx) => (
                      <Box
                        key={val}
                        sx={{
                          borderBottom:
                            idx < matrixOption.values.length - 1
                              ? '1px solid #f5f5f5'
                              : 'none',
                          py: 0.5,
                        }}
                      >
                        <FormControlLabel
                          value={val}
                          control={
                            <Radio
                              sx={{
                                color:
                                  invalidRows.has('other-option') ||
                                  invalidRows.has('other-option-text')
                                    ? '#d32f2f'
                                    : primaryColor,
                                '&.Mui-checked': { color: primaryColor },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{ fontSize: '14px', color: '#424242' }}
                            >
                              {val}
                            </Typography>
                          }
                          sx={{
                            width: '100%',
                            mx: 0,
                            my: 0,
                            py: 0.75,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                              borderRadius: 1,
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </RadioGroup>
                ) : (
                  <Box>
                    {matrixOption.values.map((val, idx) => (
                      <Box
                        key={val}
                        sx={{
                          borderBottom:
                            idx < matrixOption.values.length - 1
                              ? '1px solid #f5f5f5'
                              : 'none',
                          py: 0.5,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                formValues.selectionValuesMatriz?.[
                                  otherOptionText.trim() || 'temp-other-option'
                                ]?.includes(val) || false
                              }
                              onChange={(e) =>
                                handleOtherOptionCheckboxChangeDynamic(
                                  val,
                                  e.target.checked,
                                )
                              }
                              sx={{
                                color:
                                  invalidRows.has('other-option') ||
                                  invalidRows.has('other-option-text')
                                    ? '#d32f2f'
                                    : primaryColor,
                                '&.Mui-checked': { color: primaryColor },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{ fontSize: '14px', color: '#424242' }}
                            >
                              {val}
                            </Typography>
                          }
                          sx={{
                            width: '100%',
                            mx: 0,
                            my: 0,
                            py: 0.75,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.02)',
                              borderRadius: 1,
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Botones de acción - Dinámicas */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexDirection: 'column' }}>
            <Button
              startIcon={<AddIcon />}
              onClick={addDynamicRow}
              variant="contained"
              size="medium"
              disabled={dynamicRows.length >= matrixOption.options.length}
              fullWidth
              sx={{
                py: 1.2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
              }}
            >
              Agregar fila
            </Button>

            {hasOtherOption && (
              <Button
                startIcon={showOtherOption ? <DeleteIcon /> : <AddIcon />}
                onClick={toggleOtherOptionDynamic}
                variant={showOtherOption ? 'outlined' : 'contained'}
                color={showOtherOption ? 'error' : 'primary'}
                size="medium"
                fullWidth
                sx={{
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: showOtherOption
                    ? 'none'
                    : '0 2px 8px rgba(25, 118, 210, 0.2)',
                }}
              >
                {showOtherOption ? 'Quitar otra opción' : 'Añadir otra opción'}
              </Button>
            )}
          </Box>
        </Box>
      );
    };

    // ============================================
    // RENDER DESKTOP - MATRIZ FIJA
    // ============================================

    const renderFixedMatrixDesktop = () => (
      <Box
        sx={{
          overflowX: 'auto',
          mt: 2,
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          '&::-webkit-scrollbar': { height: '6px' },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': { backgroundColor: '#a8a8a8' },
          },
        }}
      >
        <table
          style={{
            width: '100%',
            minWidth: '600px',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  position: 'sticky',
                  left: 0,
                  backgroundColor: '#f8f9fa',
                  zIndex: 10,
                  minWidth: '200px',
                  maxWidth: '200px',
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                Opciones
              </th>
              {matrixOption.values.map((value) => (
                <th
                  key={value}
                  style={{
                    padding: '12px 20px',
                    textAlign: 'center',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    maxWidth: '160px',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                    verticalAlign: 'top',
                  }}
                  title={value}
                >
                  {value}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrixOption.options.map((option) => {
              const isRowInvalid = invalidRows.has(option);
              return (
                <tr
                  key={option}
                  style={{
                    backgroundColor: isRowInvalid ? '#ffebee' : 'transparent',
                  }}
                >
                  <td
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #e0e0e0',
                      fontWeight: '500',
                      color: isRowInvalid ? '#d32f2f' : 'inherit',
                      borderLeft: isRowInvalid
                        ? '3px solid #d32f2f'
                        : '3px solid transparent',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: isRowInvalid ? '#ffebee' : '#f8f9fa',
                      zIndex: 5,
                      minWidth: '200px',
                      maxWidth: '200px',
                      boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                      whiteSpace: 'normal',
                      verticalAlign: 'top',
                    }}
                    title={option}
                  >
                    {option}
                  </td>
                  {matrixOption.values.map((value) => (
                    <td
                      key={value}
                      style={{
                        textAlign: 'center',
                        padding: '12px 20px',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                      }}
                    >
                      {isRadio ? (
                        <Radio
                          checked={
                            formValues.selectionValuesMatriz?.[
                              option
                            ]?.includes(value) || false
                          }
                          onChange={() => handleFixedRadioChange(option, value)}
                          sx={{
                            color: isRowInvalid ? '#d32f2f' : primaryColor,
                            '&.Mui-checked': { color: primaryColor },
                            padding: '9px',
                          }}
                        />
                      ) : (
                        <Checkbox
                          checked={
                            formValues.selectionValuesMatriz?.[
                              option
                            ]?.includes(value) || false
                          }
                          onChange={(e) =>
                            handleFixedCheckboxChange(
                              option,
                              value,
                              e.target.checked,
                            )
                          }
                          sx={{
                            color: isRowInvalid ? '#d32f2f' : primaryColor,
                            '&.Mui-checked': { color: primaryColor },
                            padding: '9px',
                          }}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}

            {/* Otras opciones fijas - Desktop */}
            {otherOptions.map((otherOpt) => {
              const key =
                otherOpt.text.trim() || `temp-other-option-${otherOpt.id}`;
              const isOtherRowInvalid =
                invalidRows.has(`other-option-${otherOpt.id}`) ||
                invalidRows.has(`other-option-text-${otherOpt.id}`);

              return (
                <tr
                  key={`other-option-${otherOpt.id}`}
                  style={{
                    backgroundColor: isOtherRowInvalid ? '#ffebee' : '#f0f7ff',
                  }}
                >
                  <td
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #e0e0e0',
                      fontWeight: '500',
                      color: isOtherRowInvalid ? '#d32f2f' : '#1976d2',
                      borderLeft: isOtherRowInvalid
                        ? '3px solid #d32f2f'
                        : '3px solid #2196f3',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: isOtherRowInvalid
                        ? '#ffebee'
                        : '#f0f7ff',
                      zIndex: 5,
                      minWidth: '200px',
                      maxWidth: '200px',
                      boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                      whiteSpace: 'normal',
                      verticalAlign: 'top',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: isOtherRowInvalid ? '#d32f2f' : '#1976d2',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {otherOptionDescription}:
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Especificar..."
                        value={otherOpt.text}
                        onChange={(e) =>
                          handleOtherOptionTextChangeFixed(
                            otherOpt.id,
                            e.target.value,
                          )
                        }
                        error={invalidRows.has(
                          `other-option-text-${otherOpt.id}`,
                        )}
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                          },
                        }}
                      />
                    </Box>
                  </td>
                  {matrixOption.values.map((value) => (
                    <td
                      key={value}
                      style={{
                        textAlign: 'center',
                        padding: '12px 20px',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                      }}
                    >
                      {isRadio ? (
                        <Radio
                          checked={
                            formValues.selectionValuesMatriz?.[key]?.includes(
                              value,
                            ) || false
                          }
                          onChange={() =>
                            handleOtherOptionRadioChangeFixed(
                              otherOpt.id,
                              value,
                            )
                          }
                          sx={{
                            color: isOtherRowInvalid ? '#d32f2f' : primaryColor,
                            '&.Mui-checked': { color: primaryColor },
                            padding: '9px',
                          }}
                        />
                      ) : (
                        <Checkbox
                          checked={
                            formValues.selectionValuesMatriz?.[key]?.includes(
                              value,
                            ) || false
                          }
                          onChange={(e) =>
                            handleOtherOptionCheckboxChangeFixed(
                              otherOpt.id,
                              value,
                              e.target.checked,
                            )
                          }
                          sx={{
                            color: isOtherRowInvalid ? '#d32f2f' : primaryColor,
                            '&.Mui-checked': { color: primaryColor },
                            padding: '9px',
                          }}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    );

    // ============================================
    // RENDER DESKTOP - MATRIZ DINÁMICA
    // ============================================

    const renderDynamicMatrixDesktop = () => (
      <Box
        sx={{
          overflowX: 'auto',
          mt: 2,
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          '&::-webkit-scrollbar': { height: '6px' },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': { backgroundColor: '#a8a8a8' },
          },
        }}
      >
        <table
          style={{
            width: '100%',
            minWidth: '600px',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: '12px',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0',
                  position: 'sticky',
                  left: 0,
                  backgroundColor: '#f8f9fa',
                  zIndex: 10,
                  minWidth: '250px',
                  maxWidth: '250px',
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                Seleccionar Opción
              </th>
              {matrixOption.values.map((value) => (
                <th
                  key={value}
                  style={{
                    padding: '12px 20px',
                    textAlign: 'center',
                    borderBottom: '2px solid #e0e0e0',
                    fontWeight: '600',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    maxWidth: '160px',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                    verticalAlign: 'top',
                  }}
                  title={value}
                >
                  {value}
                </th>
              ))}
              <th
                style={{
                  padding: '12px',
                  textAlign: 'center',
                  borderBottom: '2px solid #e0e0e0',
                  fontWeight: '600',
                  minWidth: '80px',
                  backgroundColor: '#fff',
                  fontSize: '14px',
                }}
              >
                Acción
              </th>
            </tr>
          </thead>
          <tbody>
            {dynamicRows.map((row) => {
              const availableOptions = getAvailableOptions(
                row.id,
                matrixOption.options,
              );
              const isRowInvalid =
                invalidRows.has(row.selectedOption) ||
                invalidRows.has(`row-${row.id}`);

              return (
                <tr
                  key={row.id}
                  style={{
                    backgroundColor: isRowInvalid ? '#ffebee' : 'transparent',
                  }}
                >
                  <td
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #e0e0e0',
                      borderLeft: isRowInvalid
                        ? '3px solid #d32f2f'
                        : '3px solid transparent',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: isRowInvalid ? '#ffebee' : '#f8f9fa',
                      zIndex: 5,
                      minWidth: '250px',
                      maxWidth: '250px',
                      boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Autocomplete
                      options={availableOptions}
                      value={row.selectedOption || null}
                      onChange={(_, newValue) =>
                        handleDynamicSelectChange(row.id, newValue)
                      }
                      componentsProps={{
                        paper: {
                          sx: {
                            fontFamily,
                            '& .MuiAutocomplete-option': {
                              fontFamily,
                            },
                          },
                        },
                      }}
                      fullWidth
                      disableClearable
                      getOptionLabel={(option) => option || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Seleccionar..."
                          size="small"
                          error={isRowInvalid}
                        />
                      )}
                    />
                  </td>
                  {matrixOption.values.map((val) => (
                    <td
                      key={val}
                      style={{
                        textAlign: 'center',
                        padding: '12px 20px',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#fff',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                      }}
                    >
                      {row.selectedOption && (
                        <>
                          {isRadio ? (
                            <Radio
                              checked={
                                formValues.selectionValuesMatriz?.[
                                  row.selectedOption
                                ]?.includes(val) || false
                              }
                              onChange={() =>
                                handleDynamicRadioChange(row.id, val)
                              }
                              sx={{
                                color: isRowInvalid ? '#d32f2f' : primaryColor,
                                '&.Mui-checked': { color: primaryColor },
                                padding: '9px',
                              }}
                            />
                          ) : (
                            <Checkbox
                              checked={
                                formValues.selectionValuesMatriz?.[
                                  row.selectedOption
                                ]?.includes(val) || false
                              }
                              onChange={(e) =>
                                handleDynamicCheckboxChange(
                                  row.id,
                                  val,
                                  e.target.checked,
                                )
                              }
                              sx={{
                                color: isRowInvalid ? '#d32f2f' : primaryColor,
                                '&.Mui-checked': { color: primaryColor },
                                padding: '9px',
                              }}
                            />
                          )}
                        </>
                      )}
                    </td>
                  ))}
                  <td
                    style={{
                      textAlign: 'center',
                      padding: '12px',
                      borderBottom: '1px solid #e0e0e0',
                      minWidth: '80px',
                      backgroundColor: '#fff',
                      verticalAlign: 'middle',
                    }}
                  >
                    {dynamicRows.length > 1 && (
                      <IconButton
                        onClick={() => removeDynamicRow(row.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </td>
                </tr>
              );
            })}

            {/* Otra opción dinámica - Desktop */}
            {showOtherOption && hasOtherOption && (
              <tr
                style={{
                  backgroundColor:
                    invalidRows.has('other-option') ||
                    invalidRows.has('other-option-text')
                      ? '#ffebee'
                      : '#f0f7ff',
                }}
              >
                <td
                  style={{
                    padding: '12px',
                    borderBottom: '1px solid #e0e0e0',
                    borderLeft:
                      invalidRows.has('other-option') ||
                      invalidRows.has('other-option-text')
                        ? '3px solid #d32f2f'
                        : '3px solid #2196f3',
                    position: 'sticky',
                    left: 0,
                    backgroundColor:
                      invalidRows.has('other-option') ||
                      invalidRows.has('other-option-text')
                        ? '#ffebee'
                        : '#f0f7ff',
                    zIndex: 5,
                    minWidth: '250px',
                    maxWidth: '250px',
                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color:
                          invalidRows.has('other-option') ||
                          invalidRows.has('other-option-text')
                            ? '#d32f2f'
                            : '#1976d2',
                        minWidth: '80px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {otherOptionDescription}:
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Especificar..."
                      value={otherOptionText}
                      onChange={(e) =>
                        handleOtherOptionTextChangeDynamic(e.target.value)
                      }
                      error={invalidRows.has('other-option-text')}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                        },
                      }}
                    />
                  </Box>
                </td>
                {matrixOption.values.map((val) => (
                  <td
                    key={val}
                    style={{
                      textAlign: 'center',
                      padding: '12px 20px',
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: '#fff',
                      whiteSpace: 'nowrap',
                      verticalAlign: 'middle',
                    }}
                  >
                    {isRadio ? (
                      <Radio
                        checked={
                          formValues.selectionValuesMatriz?.[
                            otherOptionText.trim() || 'temp-other-option'
                          ]?.includes(val) || false
                        }
                        onChange={() =>
                          handleOtherOptionRadioChangeDynamic(val)
                        }
                        sx={{
                          color:
                            invalidRows.has('other-option') ||
                            invalidRows.has('other-option-text')
                              ? '#d32f2f'
                              : primaryColor,
                          '&.Mui-checked': { color: primaryColor },
                          padding: '9px',
                        }}
                      />
                    ) : (
                      <Checkbox
                        checked={
                          formValues.selectionValuesMatriz?.[
                            otherOptionText.trim() || 'temp-other-option'
                          ]?.includes(val) || false
                        }
                        onChange={(e) =>
                          handleOtherOptionCheckboxChangeDynamic(
                            val,
                            e.target.checked,
                          )
                        }
                        sx={{
                          color:
                            invalidRows.has('other-option') ||
                            invalidRows.has('other-option-text')
                              ? '#d32f2f'
                              : primaryColor,
                          '&.Mui-checked': { color: primaryColor },
                          padding: '9px',
                        }}
                      />
                    )}
                  </td>
                ))}
                <td
                  style={{
                    textAlign: 'center',
                    padding: '12px',
                    borderBottom: '1px solid #e0e0e0',
                    minWidth: '80px',
                    backgroundColor: '#fff',
                    verticalAlign: 'middle',
                  }}
                >
                  <IconButton
                    onClick={toggleOtherOptionDynamic}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Botones de acción - Desktop Dinámicas */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            startIcon={<AddIcon />}
            onClick={addDynamicRow}
            variant="contained"
            disabled={dynamicRows.length >= matrixOption.options.length}
          >
            Agregar fila
          </Button>

          {hasOtherOption && (
            <Button
              startIcon={showOtherOption ? <DeleteIcon /> : <AddIcon />}
              onClick={toggleOtherOptionDynamic}
              variant={showOtherOption ? 'outlined' : 'contained'}
              color={showOtherOption ? 'error' : 'primary'}
            >
              {showOtherOption ? 'Quitar otra opción' : 'Añadir otra opción'}
            </Button>
          )}
        </Box>
      </Box>
    );

    // ============================================
    // RENDER PRINCIPAL
    // ============================================

    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {isFixed && (
          <>
            {isMobile ? renderFixedMatrixMobile() : renderFixedMatrixDesktop()}

            {hasOtherOption && !isMobile && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  startIcon={
                    otherOptions.length > 0 ? <DeleteIcon /> : <AddIcon />
                  }
                  onClick={() => {
                    if (otherOptions.length > 0) {
                      removeOtherOptionFixed(
                        otherOptions[otherOptions.length - 1].id,
                      );
                    } else {
                      addOtherOptionFixed();
                    }
                  }}
                  variant={otherOptions.length > 0 ? 'outlined' : 'contained'}
                  color={otherOptions.length > 0 ? 'error' : 'primary'}
                >
                  {otherOptions.length > 0
                    ? 'Quitar otra opción'
                    : '¿Desea añadir otra opción?'}
                </Button>
              </Box>
            )}

            {hasOtherOption && isMobile && (
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={
                    otherOptions.length > 0 ? <DeleteIcon /> : <AddIcon />
                  }
                  onClick={() => {
                    if (otherOptions.length > 0) {
                      removeOtherOptionFixed(
                        otherOptions[otherOptions.length - 1].id,
                      );
                    } else {
                      addOtherOptionFixed();
                    }
                  }}
                  variant={otherOptions.length > 0 ? 'outlined' : 'contained'}
                  color={otherOptions.length > 0 ? 'error' : 'primary'}
                  fullWidth
                  size="medium"
                  sx={{
                    py: 1.2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow:
                      otherOptions.length > 0
                        ? 'none'
                        : '0 2px 8px rgba(25, 118, 210, 0.2)',
                  }}
                >
                  {otherOptions.length > 0
                    ? 'Quitar otra opción'
                    : 'Añadir otra opción'}
                </Button>
              </Box>
            )}
          </>
        )}

        {isDynamic && (
          <>
            {isMobile
              ? renderDynamicMatrixMobile()
              : renderDynamicMatrixDesktop()}
          </>
        )}
      </Box>
    );
  },
);

MatrizQuestion.displayName = 'MatrizQuestion';

export default MatrizQuestion;
